import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fineTuningService, FineTuningJob, ModelRegistry, PendingApprovals } from '../services/fineTuningService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faPlay, faCheckCircle, faTimesCircle, faClock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import TrainingStatusBanner from '../components/TrainingStatusBanner';
import ManualQAPanel from '../components/learned_qa/ManualQAPanel';
import BulkUploadPanel from '../components/learned_qa/BulkUploadPanel';
import TrainingDataBrowser from '../components/learned_qa/TrainingDataBrowser';
import ModelVersionSwitcher from '../components/learned_qa/ModelVersionSwitcher';
import { getApiBaseUrl } from '../services/api';
import './FineTuningDashboard.css';

interface DashboardData {
  jobs: FineTuningJob[];
  model_registry: ModelRegistry | null;
  pending_approvals: PendingApprovals;
}

const FineTuningDashboard: React.FC = () => {
  const { clientId: paramClientId } = useParams<{ clientId?: string }>();
  const clientId = paramClientId || localStorage.getItem('client_id') || '';

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [retraining, setRetraining] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [activeJob, setActiveJob] = useState<FineTuningJob | null>(null);
  const [lastCheckedTime, setLastCheckedTime] = useState<Date | null>(null);

  // Modal states
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'training-data' | 'models'>('overview');

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef<string | null>(null);
  const lastLoadTimeRef = useRef<number>(0);
  const errorCountRef = useRef<number>(0);
  const activeJobRef = useRef<FineTuningJob | null>(null);
  const LOAD_THROTTLE_MS = 2000; // Minimum 2 seconds between loads

  // Sync activeJob to ref
  useEffect(() => {
    activeJobRef.current = activeJob;
  }, [activeJob]);

  // Load initial dashboard data efficiently
  const loadDashboardData = useCallback(async (silent = false) => {
    if (!clientId) return;

    // Throttle rapid calls
    const now = Date.now();
    if (now - lastLoadTimeRef.current < LOAD_THROTTLE_MS) {
      console.log('Throttling dashboard data load - too frequent');
      return;
    }
    lastLoadTimeRef.current = now;

    try {
      if (!silent) {
        setLoading(true);
      }
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(`${getApiBaseUrl()}/fine-tuning/${clientId}/dashboard-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load dashboard data: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Check for job completion before updating state
      const prevActiveJob = activeJobRef.current;
      const newActiveJob = data.jobs.find((j: FineTuningJob) =>
        j.status === 'pending' || j.status === 'running'
      );
      
      // Detect job completion
      if (prevActiveJob && prevActiveJob.job_id && 
          (prevActiveJob.status === 'pending' || prevActiveJob.status === 'running')) {
        const completedJob = data.jobs.find((j: FineTuningJob) => 
          j.job_id === prevActiveJob.job_id && 
          (j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled')
        );
        
        if (completedJob) {
          // Job completed
          if (completedJob.status === 'completed') {
            toast.success('Training completed successfully!');
          } else {
            toast.info(`Training ${completedJob.status}`);
          }
        }
      }
      
      setDashboardData(data);
      
      // Update active job - only update if it actually changed
      setActiveJob(prev => {
        const newJobId = newActiveJob?.job_id || null;
        const prevJobId = prev?.job_id || null;
        
        // Only update if job_id changed or job became null
        if (newJobId !== prevJobId) {
          return newActiveJob || null;
        }
        // Same job - return previous to prevent unnecessary re-render
        return prev;
      });

      setLastUpdateTime(new Date());
      setLastCheckedTime(new Date());
      errorCountRef.current = 0; // Reset error count on success
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      // Only show toast on manual refresh, not during polling
      if (!silent) {
        toast.error(error.message || 'Failed to load dashboard data');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [clientId]);

  // Calculate smart polling interval based on job status and duration
  const getPollingInterval = useCallback((job: FineTuningJob | null): number => {
    if (!job || !job.job_id) {
      return 0; // No polling if no active job
    }

    // Stop polling if job is completed or failed
    if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
      return 0;
    }

    // Pending/Queued: Poll every 8 seconds (status changes quickly)
    if (job.status === 'pending') {
      return 8000;
    }

    // Running: Adaptive interval based on duration
    if (job.status === 'running' && job.started_at) {
      const startedAt = new Date(job.started_at).getTime();
      const now = Date.now();
      const durationMinutes = (now - startedAt) / (1000 * 60);

      // Running 0-10 minutes: Poll every 30 seconds
      if (durationMinutes < 10) {
        return 30000;
      }
      // Running 10+ minutes: Poll every 3 minutes (job is stable)
      return 180000;
    }

    // Default: 30 seconds for running jobs without started_at
    return 30000;
  }, []);

  // Smart polling with adaptive intervals
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (!clientId) {
      return;
    }

    const interval = getPollingInterval(activeJob);
    
    // If interval is 0, don't start polling
    if (interval === 0) {
      return;
    }

    // Set up polling
    pollingIntervalRef.current = setInterval(() => {
      // Check if tab is visible (stop polling when tab is hidden)
      if (document.hidden) {
        return;
      }

      // Silent refresh (don't show loading spinner)
      loadDashboardData(true).catch(error => {
        console.error('Error during polling:', error);
        errorCountRef.current++;
        
        // Exponential backoff on errors
        if (errorCountRef.current > 3) {
          // Too many errors, stop polling temporarily
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Retry after 30 seconds
          setTimeout(() => {
            errorCountRef.current = 0;
            // Restart polling by triggering effect
            setLastCheckedTime(new Date());
          }, 30000);
        }
      });
    }, interval);

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [clientId, activeJob?.job_id, activeJob?.status, activeJob?.started_at, getPollingInterval, loadDashboardData]);

  // Handle page visibility changes (stop/resume polling)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else {
        // Tab visible - resume polling if there's an active job
        const interval = getPollingInterval(activeJob);
        if (interval > 0 && !pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(() => {
            if (!document.hidden) {
              loadDashboardData(true).catch(console.error);
            }
          }, interval);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeJob, getPollingInterval, loadDashboardData]);

  // Initial load - only once when clientId changes
  useEffect(() => {
    if (clientId && hasLoadedRef.current !== clientId) {
      hasLoadedRef.current = clientId;
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleRetrain = async () => {
    if (!clientId) {
      toast.error('Client ID not found');
      return;
    }

    try {
      setRetraining(true);
      await fineTuningService.triggerRetraining(clientId);
      toast.success('Training job started successfully');

      // Reload data after a short delay to get the new job
      setTimeout(() => {
        loadDashboardData(false); // Show loading spinner
      }, 2000);
    } catch (error: any) {
      console.error('Error triggering retraining:', error);
      toast.error(error.message || 'Failed to trigger retraining');
    } finally {
      setRetraining(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#ef4444' }} />;
      case 'cancelled':
        return <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#6b7280' }} />;
      case 'pending':
        return <FontAwesomeIcon icon={faClock} style={{ color: '#f59e0b' }} />;
      case 'running':
        return <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#3b82f6' }} />;
      default:
        return null;
    }
  };

  const handleModalSuccess = () => {
    // Reload dashboard data after adding training data
    loadDashboardData(false);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="fine-tuning-dashboard">
      {/* Professional Status Banner at Top */}
      <TrainingStatusBanner
        activeJob={activeJob}
        lastUpdateTime={lastUpdateTime}
      />

      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div>
          <h1>Fine-Tuning Dashboard</h1>
          <p>AI Model Training Management</p>
        </div>

        <div className="header-actions">
          {lastCheckedTime && (
            <span className="last-checked-status" style={{
              fontSize: '12px',
              color: '#6b7280',
              marginRight: '12px'
            }}>
              Last checked: {(() => {
                const seconds = Math.floor((Date.now() - lastCheckedTime.getTime()) / 1000);
                if (seconds < 60) return `${seconds}s ago`;
                const minutes = Math.floor(seconds / 60);
                if (minutes < 60) return `${minutes}m ago`;
                const hours = Math.floor(minutes / 60);
                return `${hours}h ago`;
              })()}
            </span>
          )}

          <button
            onClick={() => setIsManualModalOpen(true)}
            className="btn-secondary"
            title="Add individual Q&A pair"
          >
            + Add Manual Q&A
          </button>

          <button
            onClick={() => setIsBulkModalOpen(true)}
            className="btn-secondary"
            title="Upload multiple Q&A pairs at once"
          >
            📤 Bulk Upload
          </button>

          <button
            onClick={() => loadDashboardData(false)}
            disabled={loading}
            className="btn-refresh"
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            Refresh
          </button>

          <button
            onClick={handleRetrain}
            disabled={!clientId || retraining || !dashboardData?.pending_approvals?.can_train}
            className="btn-primary"
          >
            <FontAwesomeIcon icon={faPlay} />
            Start Training
          </button>
        </div>
      </div>

      {/* Panels */}
      <ManualQAPanel
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        onSuccess={handleModalSuccess}
        clientId={clientId}
      />

      <BulkUploadPanel
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSuccess={handleModalSuccess}
        clientId={clientId}
      />

      {/* Tabs Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'training-data' ? 'active' : ''}`}
          onClick={() => setActiveTab('training-data')}
        >
          Training Data
        </button>
        <button
          className={`tab-button ${activeTab === 'models' ? 'active' : ''}`}
          onClick={() => setActiveTab('models')}
        >
          Model Versions
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'training-data' && (
        <TrainingDataBrowser
          clientId={clientId}
          onDataChange={() => loadDashboardData(false)}
        />
      )}

      {activeTab === 'models' && (
        <ModelVersionSwitcher
          clientId={clientId}
          onModelChange={() => loadDashboardData(false)}
        />
      )}

      {/* Dashboard Content - Overview Tab */}
      {activeTab === 'overview' && dashboardData && (
        <div className="dashboard-grid">
          {/* Model Registry Card */}
          <div className="dashboard-card">
            <h2>Active Model</h2>
            {dashboardData.model_registry && dashboardData.model_registry.current_model ? (
              <div className="model-info">
                <div className="model-status">
                  <span className={`status-badge ${dashboardData.model_registry.status || 'inactive'}`}>
                    {dashboardData.model_registry.status === 'active' ? 'Active' : 
                     dashboardData.model_registry.status || 'Inactive'}
                  </span>
                </div>
                <div className="model-details">
                  <div className="detail-row">
                    <span>Fine-Tuned Model:</span>
                    <code style={{ 
                      color: dashboardData.model_registry.status === 'active' ? '#10b981' : '#6b7280',
                      fontWeight: 'bold'
                    }}>
                      {dashboardData.model_registry.current_model}
                    </code>
                  </div>
                  <div className="detail-row">
                    <span>Base Model:</span>
                    <code>{dashboardData.model_registry.base_model || 'gpt-4o'}</code>
                  </div>
                  <div className="detail-row">
                    <span>Training Examples:</span>
                    <strong>{dashboardData.model_registry.training_examples_count || 0}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Last Trained:</span>
                    <span>
                      {dashboardData.model_registry.last_trained_at
                        ? new Date(dashboardData.model_registry.last_trained_at).toLocaleString()
                        : 'Never'}
                    </span>
                  </div>
                  {dashboardData.model_registry.status !== 'active' && (
                    <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                      <small style={{ color: '#6b7280', fontStyle: 'italic' }}>
                        Model is not currently active. Chat will use base model.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>No fine-tuned model registered</p>
                <small>Train your first model to get started</small>
              </div>
            )}
          </div>

          {/* Pending Approvals Card */}
          <div className="dashboard-card">
            <h2>Training Queue</h2>
            <div className="queue-info">
              <div className="queue-count">
                <strong>{dashboardData.pending_approvals.pending_count}</strong>
                <span>Pending Q&A Pairs</span>
              </div>

              {dashboardData.pending_approvals.can_train ? (
                <div className="ready-badge">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Ready to Train
                </div>
              ) : (
                <div className="needs-more">
                  <small>Need {dashboardData.pending_approvals.needed} more examples</small>
                  <div className="progress-mini">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${(dashboardData.pending_approvals.pending_count / dashboardData.pending_approvals.min_examples) * 100}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Jobs */}
          <div className="dashboard-card full-width">
            <h2>Training History</h2>
            {dashboardData.jobs.length === 0 ? (
              <div className="empty-state">
                <p>No training jobs yet</p>
              </div>
            ) : (
              <div className="jobs-table">
                {dashboardData.jobs.map((job) => {
                  const isCompleted = job.status === 'completed';
                  const isFailed = job.status === 'failed' || job.status === 'cancelled';
                  const isRunning = job.status === 'running';
                  const isPending = job.status === 'pending';
                  
                  return (
                    <div 
                      key={job.id} 
                      className="job-row"
                      style={{
                        opacity: isCompleted ? 1 : isFailed ? 0.8 : 1,
                        borderLeft: isCompleted ? '3px solid #10b981' : 
                                   isFailed ? '3px solid #ef4444' : 
                                   isRunning ? '3px solid #3b82f6' : 
                                   isPending ? '3px solid #f59e0b' : '3px solid transparent'
                      }}
                    >
                      <div className="job-status">
                        {getStatusIcon(job.status)}
                        <span style={{ 
                          textTransform: 'capitalize',
                          fontWeight: isRunning ? 600 : 500
                        }}>
                          {job.status}
                        </span>
                      </div>
                      <div className="job-info" style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <code className="job-id" style={{ fontSize: '12px' }}>
                            {job.job_id}
                          </code>
                        </div>
                        <div className="job-meta" style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <span>
                            {job.training_examples_count || 0} examples
                          </span>
                          {job.started_at && (
                            <span>
                              Started: {new Date(job.started_at).toLocaleDateString()} {new Date(job.started_at).toLocaleTimeString()}
                            </span>
                          )}
                          {job.completed_at && (
                            <span style={{ color: isCompleted ? '#10b981' : '#ef4444' }}>
                              Completed: {new Date(job.completed_at).toLocaleDateString()} {new Date(job.completed_at).toLocaleTimeString()}
                            </span>
                          )}
                          {job.error_message && (
                            <span style={{ color: '#ef4444', fontStyle: 'italic' }}>
                              Error: {job.error_message}
                            </span>
                          )}
                        </div>
                      </div>
                      {job.fine_tuned_model && (
                        <div className="job-model" style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '4px'
                        }}>
                          <small style={{ fontSize: '10px', color: '#6b7280' }}>Model:</small>
                          <code style={{ 
                            fontSize: '12px',
                            color: isCompleted ? '#10b981' : '#6b7280',
                            fontWeight: 'bold'
                          }}>
                            {job.fine_tuned_model}
                          </code>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FineTuningDashboard;